/**
 * PaymentFlowManager — Client-side orchestration for crypto payment flows.
 *
 * Handles three chain-specific flows:
 *   - Solana: POST prepare → walletManager.signSolanaTransaction() → POST confirm
 *   - EVM: GET treasury-info → walletManager.sendERC20Transfer() → POST submit-evm → poll verify
 *   - GalaChain: POST prepare → walletManager.signGalaChainDto() → POST confirm
 *
 * Registered as a singleton via ServiceLocator in BootScene.
 */

import { getApiBaseUrl } from '../utils/ApiConfig';
import { fetchWithTimeout } from '../utils/fetchWithTimeout';
import { ServiceLocator, Services } from '../utils/ServiceLocator';
import type { WalletManager } from './WalletManager';
import type { Chain, Stablecoin, AssetType } from '../shared/common/MonetizationTypes';

const API_URL = getApiBaseUrl();

const EVM_CHAINS: Chain[] = ['ethereum', 'polygon', 'bsc'];
const PAYMENT_FETCH_TIMEOUT_MS = 8000;

const EVM_CHAIN_IDS: Record<string, string> = {
  ethereum: '0x1',
  polygon: '0x89',
  bsc: '0x38',
};

export interface PaymentRequest {
  assetType: AssetType;
  assetId?: string;
  amountUsd: number;
  chain: Chain;
  stablecoin: Stablecoin;
  metadata?: Record<string, unknown>;
}

export interface PaymentResult {
  success: boolean;
  purchaseId?: string;
  assetId?: string;
  status?: 'verified' | 'pending' | 'failed';
  error?: string;
}

export class PaymentFlowManager {
  private getSessionToken: () => string | null;

  constructor(getSessionToken: () => string | null) {
    this.getSessionToken = getSessionToken;
  }

  private authHeaders(): Record<string, string> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const token = this.getSessionToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
  }

  /** Auto-report payment errors to the bug API */
  private reportPaymentError(title: string, observed: string): void {
    try {
      const token = this.getSessionToken() || localStorage.getItem('crimsonmandate_token');
      if (!token) { console.warn('[PaymentFlowManager] No auth token for bug report'); return; }
      console.log('[PaymentFlowManager] Sending auto bug report:', title);
      fetch('/api/bugs/report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          title: `[Payment] ${title}`.slice(0, 200),
          category: 'economy',
          severity: 'major',
          observed: observed.slice(0, 500),
          logSnippet: JSON.stringify({ userAgent: navigator.userAgent, time: new Date().toISOString() }),
        }),
      }).then(r => { if (!r.ok) console.error('[PaymentFlowManager] Bug report failed:', r.status); })
        .catch(e => console.error('[PaymentFlowManager] Bug report error:', e));
    } catch (e) { console.error('[PaymentFlowManager] reportPaymentError exception:', e); }
  }

  private getWallet(): WalletManager {
    const wallet = ServiceLocator.tryGet<WalletManager>(Services.WALLET);
    if (!wallet) throw new Error('WalletManager not available');
    return wallet;
  }

  /**
   * Execute a payment for any asset type across any supported chain.
   */
  async executePayment(request: PaymentRequest): Promise<PaymentResult> {
    const { chain } = request;

    try {
      // Ensure wallet is connected for the target chain
      const wallet = this.getWallet();
      await wallet.connectForChain(chain);

      if (chain === 'solana') {
        return await this.executeSolanaPayment(request);
      } else if (EVM_CHAINS.includes(chain)) {
        return await this.executeEVMPayment(request);
      } else if (chain === 'galachain') {
        return await this.executeGalaChainPayment(request);
      } else {
        return { success: false, error: `Unsupported chain: ${chain}` };
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : (typeof error === 'object' && error !== null ? JSON.stringify(error) : String(error));

      // No EVM wallet but a Solana wallet is available — fall back to Solana
      if (message === 'NO_EVM_WALLET_SOLANA_AVAILABLE') {
        console.log('[PaymentFlowManager] No EVM wallet found, falling back to Solana chain.');
        try {
          const wallet = this.getWallet();
          request.chain = 'solana';
          await wallet.connectForChain('solana');
          return await this.executeSolanaPayment(request);
        } catch (fallbackError) {
          const fallbackMsg = fallbackError instanceof Error ? fallbackError.message : String(fallbackError);
          console.error('[PaymentFlowManager] Solana fallback failed:', fallbackMsg);
          return { success: false, error: fallbackMsg };
        }
      }

      console.error('[PaymentFlowManager] Payment failed:', message);
      // Don't auto-report known non-bug conditions (user just doesn't have a wallet)
      const nonBugPatterns = ['No Solana wallet', 'No EVM wallet', 'wallet not connected', 'not detected', 'User rejected', 'User denied', 'user rejected', 'insufficient balance', 'positive number', 'rejected the request', 'InstructionError'];
      if (!nonBugPatterns.some(p => message.includes(p))) {
        this.reportPaymentError('Payment failed', message);
      }
      return { success: false, error: message };
    }
  }

  // ============ Solana Flow ============

  private async executeSolanaPayment(request: PaymentRequest): Promise<PaymentResult> {
    try {
    const wallet = this.getWallet();
    const walletAddress = wallet.getSolanaAddress();
    if (!walletAddress) throw new Error('Solana wallet not connected');

    // 1. Prepare transaction
    const prepareRes = await fetchWithTimeout(`${API_URL}/payment/prepare-solana`, {
      method: 'POST',
      headers: this.authHeaders(),
      body: JSON.stringify({
        assetType: request.assetType,
        assetId: request.assetId,
        amountUsd: request.amountUsd,
        stablecoin: request.stablecoin,
        walletAddress,
        metadata: request.metadata,
      }),
    }, PAYMENT_FETCH_TIMEOUT_MS);

    const prepareData = await prepareRes.json();
    if (!prepareRes.ok || !prepareData.success) {
      const err = prepareData.error || 'Failed to prepare Solana transaction';
      this.reportPaymentError('Solana prepare failed', err);
      return { success: false, error: err };
    }

    // 2. Sign with wallet
    const signResult = await wallet.signSolanaTransaction(prepareData.serializedMessage);

    // 3. Confirm with server
    const confirmRes = await fetchWithTimeout(`${API_URL}/payment/confirm-solana`, {
      method: 'POST',
      headers: this.authHeaders(),
      body: JSON.stringify({
        purchaseId: prepareData.purchaseId,
        signature: signResult.signature,
        publicKey: signResult.publicKey,
      }),
    }, PAYMENT_FETCH_TIMEOUT_MS);

    const confirmData = await confirmRes.json();
    if (!confirmRes.ok) {
      const err = confirmData.error || 'Solana confirmation failed';
      this.reportPaymentError('Solana confirm failed', err);
      return { success: false, error: err };
    }

    return {
      success: true,
      purchaseId: confirmData.purchaseId,
      assetId: confirmData.assetId,
      status: confirmData.status,
    };
    } catch (err: any) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error('[PaymentFlowManager] executeSolanaPayment error:', msg);
      this.reportPaymentError('Solana payment error', msg);
      return { success: false, error: msg };
    }
  }

  // ============ EVM Flow ============

  private async executeEVMPayment(request: PaymentRequest): Promise<PaymentResult> {
    const wallet = this.getWallet();
    const walletAddress = await wallet.getEVMAddress();

    // 1. Get treasury info for this chain
    const infoRes = await fetchWithTimeout(`${API_URL}/payment/treasury-info?chain=${request.chain}`, {
      headers: this.authHeaders(),
    }, PAYMENT_FETCH_TIMEOUT_MS);
    const infoData = await infoRes.json();
    if (!infoRes.ok || !infoData.success) {
      return { success: false, error: infoData.error || 'Failed to get treasury info' };
    }

    const treasuryAddress = infoData.data.treasuryAddress;
    const tokenAddress = request.stablecoin === 'USDC'
      ? infoData.data.usdcAddress
      : infoData.data.usdtAddress;

    if (!treasuryAddress) {
      return { success: false, error: `No treasury address configured for ${request.chain}` };
    }

    // 2. Build and send ERC-20 transfer via MetaMask
    // All chains use 6-decimal stablecoins (BSC uses Wormhole-wrapped USDC/USDT from Solana)
    const decimals = 6;
    const amountRaw = '0x' + BigInt(Math.round(request.amountUsd * (10 ** decimals))).toString(16);
    const chainId = EVM_CHAIN_IDS[request.chain] || '0x1';

    const txHash = await wallet.sendERC20Transfer(tokenAddress, treasuryAddress, amountRaw, chainId);

    // 3. Submit tx hash to server for verification
    const submitRes = await fetchWithTimeout(`${API_URL}/payment/submit-evm`, {
      method: 'POST',
      headers: this.authHeaders(),
      body: JSON.stringify({
        assetType: request.assetType,
        assetId: request.assetId,
        amountUsd: request.amountUsd,
        chain: request.chain,
        stablecoin: request.stablecoin,
        txHash,
        walletAddress,
        metadata: request.metadata,
      }),
    }, PAYMENT_FETCH_TIMEOUT_MS);

    const submitData = await submitRes.json();
    if (!submitRes.ok) {
      return { success: false, error: submitData.error || 'EVM submission failed' };
    }

    // 4. If pending, poll for verification
    if (submitData.data?.status === 'pending') {
      return await this.pollVerification(submitData.data.purchaseId);
    }

    return {
      success: true,
      purchaseId: submitData.data?.purchaseId,
      assetId: submitData.data?.assetId,
      status: submitData.data?.status,
    };
  }

  // ============ GalaChain Flow ============

  private async executeGalaChainPayment(request: PaymentRequest): Promise<PaymentResult> {
    console.log('[PaymentFlowManager] executeGalaChainPayment started', request);
    const wallet = this.getWallet();
    const walletInfo = wallet.getWalletInfo();
    console.log('[PaymentFlowManager] walletInfo:', walletInfo);
    if (!walletInfo) throw new Error('GalaChain wallet not connected');
    if (walletInfo.chain !== 'galachain') {
      return {
        success: false,
        error: `Connected wallet is on chain "${walletInfo.chain}", not GalaChain. Please connect your Gala Wallet.`,
      };
    }
    if (typeof wallet.signGalaChainTypedData !== 'function') {
      return {
        success: false,
        error: 'Connected wallet does not support GalaChain signing. Please use the Gala Wallet extension.',
      };
    }
    const walletAddress = walletInfo.address;

    // 1. Prepare DTO
    console.log('[PaymentFlowManager] Calling prepare-galachain with wallet:', walletAddress);
    const prepareRes = await fetchWithTimeout(`${API_URL}/payment/prepare-galachain`, {
      method: 'POST',
      headers: this.authHeaders(),
      body: JSON.stringify({
        assetType: request.assetType,
        assetId: request.assetId,
        amountUsd: request.amountUsd,
        stablecoin: request.stablecoin,
        walletAddress,
        metadata: request.metadata,
      }),
    }, PAYMENT_FETCH_TIMEOUT_MS);

    const prepareData = await prepareRes.json();
    if (!prepareRes.ok || !prepareData.success) {
      return { success: false, error: prepareData.error || 'Failed to prepare GalaChain transaction' };
    }

    // 2. Sign DTO with Gala Wallet using the server-provided TransferToken EIP-712 typed data.
    // Uses WPM channel: computes EIP-712 hash client-side, sends to Gala Wallet signTypedMessage.
    if (!prepareData.typedData) {
      return { success: false, error: 'Server returned no typedData for GalaChain signing' };
    }
    let signature: string;
    try {
      signature = await wallet.signGalaChainTypedData(prepareData.typedData);
    } catch (signErr) {
      const signMsg = signErr instanceof Error ? signErr.message : String(signErr);
      console.error('[PaymentFlowManager] GalaChain signing failed:', signMsg);
      return { success: false, error: `GalaChain signing failed: ${signMsg}` };
    }

    // 3. Confirm with server — include treasuryAddress so server rebuilds the correct typedData
    // for signature verification (must match exactly what was signed)
    const confirmRes = await fetchWithTimeout(`${API_URL}/payment/confirm-galachain`, {
      method: 'POST',
      headers: this.authHeaders(),
      body: JSON.stringify({
        purchaseId: prepareData.purchaseId,
        signature,
        walletAddress,
        treasuryAddress: prepareData.treasuryAddress,
      }),
    }, PAYMENT_FETCH_TIMEOUT_MS);

    const confirmData = await confirmRes.json();
    if (!confirmRes.ok) {
      const rawErr = confirmData.error;
      const errMsg = typeof rawErr === 'string' ? rawErr : (rawErr?.message || rawErr?.Message || JSON.stringify(rawErr) || 'GalaChain confirmation failed');
      const nonBugPatterns = ['insufficient balance', 'Insufficient balance', 'not detected', 'User rejected', 'user rejected'];
      if (!nonBugPatterns.some(p => errMsg.includes(p))) {
        this.reportPaymentError('GalaChain confirm failed', errMsg);
      }
      return { success: false, error: errMsg };
    }

    return {
      success: true,
      purchaseId: confirmData.purchaseId,
      assetId: confirmData.assetId,
      status: confirmData.status,
    };
  }

  // ============ Polling ============

  private async pollVerification(purchaseId: string, maxAttempts = 30, intervalMs = 5000): Promise<PaymentResult> {
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise(r => setTimeout(r, intervalMs));

      const res = await fetchWithTimeout(`${API_URL}/payment/verify/${purchaseId}`, {
        headers: this.authHeaders(),
      }, PAYMENT_FETCH_TIMEOUT_MS);
      const data = await res.json();

      if (!res.ok) continue;

      if (data.data?.status === 'verified') {
        return {
          success: true,
          purchaseId,
          assetId: data.data.assetId,
          status: 'verified',
        };
      }
      if (data.data?.status === 'failed') {
        return { success: false, error: 'Transaction verification failed' };
      }
      // Still pending — continue polling
    }

    return { success: false, status: 'pending', error: 'Transaction verification timed out. It may still be processing.' };
  }
}
